import type { TooltipContent } from "../render/RenderContext.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { MouseEventArgs } from "../core/events.js";
import type { Point, Rect, Size } from "../../common/geometry.js";
import type { WebwinImage } from "../../engine/ImageStore.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawTriangle } from "../themes/glyphs.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { showMenu } from "./ContextMenu.js";
import { menuSeparator, parseMnemonic, type MenuItemSpec } from "./MenuItem.js";
import type { MenuPopup } from "./MenuPopup.js";

export interface ToolbarItemSpec {
  /** Caption. `&` marks a mnemonic for display, as in menus. */
  text?: string;
  icon?: WebwinImage | null;
  enabled?: boolean;
  /** Hover text for this button. Falls back to its caption in icon mode. */
  tooltip?: string;
  /** Latched state for a toggle button. */
  checked?: boolean;
  /** Make this a two-state button rather than a push button. */
  toggle?: boolean;
  /** A vertical rule. Every other field is ignored. */
  separator?: boolean;
  /**
   * Rows to drop below the button. On its own the whole button opens the
   * menu; with `split` the arrow opens it and the rest of the button clicks.
   */
  menu?: MenuItemSpec[];
  split?: boolean;
  onClick?: (item: ToolbarItemSpec) => void;
  tag?: unknown;
}

/** What a button shows: its icon, its caption, or both. */
export type ToolbarDisplay = "icon" | "text" | "both";

export interface ToolbarOptions extends ControlOptions {
  items?: ToolbarItemSpec[];
  display?: ToolbarDisplay;
  iconSize?: number;
  /** Collapse items that do not fit into a chevron menu at the right end. */
  overflow?: boolean;
}

const SEPARATOR_WIDTH = 9;
const BUTTON_PADDING = 8;
const ICON_TEXT_GAP = 6;
const ARROW_WIDTH = 14;
const CHEVRON_WIDTH = 22;
/** Room above and below a toolbar button. Leaves the default strip 34px at the default font. */
const BUTTON_HEIGHT_PADDING = 9;

/** `indexAt` returns this for the overflow chevron. */
export const TOOLBAR_OVERFLOW = -2;

/**
 * A strip of buttons: push, toggle, drop-down, split, and separators.
 *
 * Described as data like a menu, so the toolbar owns its own layout and can do
 * the thing a hand-assembled row of buttons cannot — when the strip is too
 * narrow, whatever does not fit collapses into a chevron that opens the
 * remainder as a menu.
 */
export class Toolbar extends Control {
  #items: ToolbarItemSpec[];
  #hover = -1;
  #pressed = -1;
  #menu: MenuPopup | null = null;
  #menuIndex = -1;
  /** Number of items that fit, as of the last layout. */
  #visibleCount = 0;

  display: ToolbarDisplay;
  iconSize: number;
  overflow: boolean;

  constructor(options: ToolbarOptions = {}) {
    super({ width: 400, height: 34, tabStop: false, cursor: "default", ...options });
    this.#items = options.items ?? [];
    this.display = options.display ?? "both";
    this.iconSize = options.iconSize ?? 16;
    this.overflow = options.overflow ?? true;

    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerleave", () => {
      this.#setHover(-1);
      this.#pressed = -1;
    });
    this.on("pointerdown", (e) => this.#onPointerDown(e));
    // No pointerup handler: it runs before click, so clearing the pressed
    // item there would leave click with nothing to fire.
    this.on("click", (e) => this.#onClick(e));
  }

  get items(): ToolbarItemSpec[] {
    return this.#items;
  }
  set items(v: ToolbarItemSpec[]) {
    this.#items = v;
    this.closeMenu();
    this.invalidate();
  }

  /** Items pushed into the overflow chevron by the last layout. */
  get overflowItems(): ToolbarItemSpec[] {
    return this.#items.slice(this.#visibleCount);
  }

  get hasOverflow(): boolean {
    return this.#visibleCount < this.#items.length;
  }

  /** As wide as it is given, and tall enough for whichever of icon or caption is taller. */
  override preferredSize(available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    const line = desktop.rc.lineHeight(desktop.theme.metrics.font);
    const content = this.display === "text" ? line : Math.max(this.iconSize, line);
    return { width: available.width, height: Math.ceil(content + BUTTON_HEIGHT_PADDING * 2) };
  }

  protected override onEnvironmentChanged(): void {
    this.invalidateMeasure();
  }

  // --- measurement ------------------------------------------------------

  #showsIcon(item: ToolbarItemSpec): boolean {
    return this.display !== "text" && !!item.icon;
  }

  #showsText(item: ToolbarItemSpec): boolean {
    if (this.display === "icon" && item.icon) return false;
    return !!item.text;
  }

  /** Width of one item, ignoring whether it fits. */
  itemWidth(item: ToolbarItemSpec, rc: RenderContext, theme: Theme): number {
    if (item.separator) return SEPARATOR_WIDTH;
    let w = BUTTON_PADDING * 2;
    if (this.#showsIcon(item)) w += this.iconSize;
    if (this.#showsText(item)) {
      const label = parseMnemonic(item.text ?? "").text;
      w += Math.ceil(rc.measureText(label, theme.metrics.font).width);
      if (this.#showsIcon(item)) w += ICON_TEXT_GAP;
    }
    if (item.menu) w += ARROW_WIDTH;
    return Math.max(item.separator ? SEPARATOR_WIDTH : 24, w);
  }

  /**
   * Decide how many items fit, reserving room for the chevron when some do
   * not. Called before any geometry question so the answers stay consistent.
   */
  #layout(rc: RenderContext, theme: Theme): void {
    if (!this.overflow) {
      this.#visibleCount = this.#items.length;
      return;
    }
    const widths = this.#items.map((i) => this.itemWidth(i, rc, theme));
    const total = widths.reduce((a, b) => a + b, 0);
    if (total <= this.width - 4) {
      this.#visibleCount = this.#items.length;
      return;
    }
    // Something must collapse, so the chevron is now taking up room too.
    const budget = this.width - 4 - CHEVRON_WIDTH;
    let used = 0;
    let count = 0;
    for (const w of widths) {
      if (used + w > budget) break;
      used += w;
      count++;
    }
    // A trailing separator would sit against the chevron looking orphaned.
    while (count > 0 && this.#items[count - 1]!.separator) count--;
    this.#visibleCount = count;
  }

  itemRect(index: number, rc: RenderContext, theme: Theme): Rect {
    let x = 2;
    for (let i = 0; i < index; i++) x += this.itemWidth(this.#items[i]!, rc, theme);
    const w = index < this.#items.length ? this.itemWidth(this.#items[index]!, rc, theme) : 0;
    return { x, y: 2, width: w, height: this.height - 4 };
  }

  get chevronRect(): Rect {
    return { x: this.width - CHEVRON_WIDTH - 2, y: 2, width: CHEVRON_WIDTH, height: this.height - 4 };
  }

  /**
   * Per-button hover text. In icon-only mode a button with no explicit
   * tooltip falls back to its caption, which is the whole reason an icon
   * toolbar is usable at all.
   */
  override getTooltip(p: Point): TooltipContent | null {
    const d = this.desktop;
    if (!d) return this.tooltip;
    const index = this.indexAt(p, d.rc, d.theme);
    if (index === TOOLBAR_OVERFLOW) return "More";
    const item = this.#items[index];
    if (!item) return this.tooltip;
    if (item.tooltip) return item.tooltip;
    if (!this.#showsText(item) && item.text) return parseMnemonic(item.text).text;
    return null;
  }

  /** Item under a local point: an index, TOOLBAR_OVERFLOW, or -1. */
  indexAt(p: Point, rc: RenderContext, theme: Theme): number {
    this.#layout(rc, theme);
    if (p.y < 0 || p.y > this.height) return -1;
    if (this.hasOverflow) {
      const c = this.chevronRect;
      if (p.x >= c.x && p.x < c.x + c.width) return TOOLBAR_OVERFLOW;
    }
    let x = 2;
    for (let i = 0; i < this.#visibleCount; i++) {
      const w = this.itemWidth(this.#items[i]!, rc, theme);
      if (p.x >= x && p.x < x + w) return this.#items[i]!.separator ? -1 : i;
      x += w;
    }
    return -1;
  }

  /** True when `x` (local) falls in a split button's arrow half. */
  #onArrow(index: number, x: number, rc: RenderContext, theme: Theme): boolean {
    const item = this.#items[index];
    if (!item?.menu) return false;
    if (!item.split) return true; // the whole button opens the menu
    const r = this.itemRect(index, rc, theme);
    return x >= r.x + r.width - ARROW_WIDTH;
  }

  // --- menus ------------------------------------------------------------

  get openMenuIndex(): number {
    return this.#menuIndex;
  }

  /**
   * Convert toolbar items into menu rows for the overflow chevron.
   *
   * Separators are tidied on the way: the run that overflows can begin or end
   * on one, and a menu that opens with a rule across the top looks broken.
   */
  toMenuItems(items: ToolbarItemSpec[]): MenuItemSpec[] {
    let start = 0;
    let end = items.length;
    while (start < end && items[start]!.separator) start++;
    while (end > start && items[end - 1]!.separator) end--;

    const rows: MenuItemSpec[] = [];
    for (const item of items.slice(start, end)) {
      if (item.separator) {
        // Collapse a run of separators left by hidden items into one.
        if (rows[rows.length - 1] === menuSeparator) continue;
        rows.push(menuSeparator);
        continue;
      }
      rows.push(this.#toMenuItem(item));
    }
    return rows;
  }

  #toMenuItem(item: ToolbarItemSpec): MenuItemSpec {
    return ((): MenuItemSpec => {
      const row: MenuItemSpec = {
        text: item.text ?? "",
        icon: item.icon ?? null,
        enabled: item.enabled,
        checked: item.checked,
        tag: item.tag,
      };
      if (item.menu) row.items = item.menu;
      else row.onClick = () => this.#invoke(item);
      return row;
    })();
  }

  openMenu(index: number): void {
    const desktop = this.desktop;
    const item = this.#items[index];
    if (!desktop || !item?.menu || item.enabled === false) return;
    if (this.#menuIndex === index) return;
    this.closeMenu();

    const r = this.itemRect(index, desktop.rc, desktop.theme);
    const anchor = this.screenBounds;
    const menu = showMenu(desktop, item.menu, {
      x: anchor.x + r.x,
      y: anchor.y + anchor.height,
    });
    if (!menu) return;
    menu.on("closed", () => {
      if (this.#menu === menu) {
        this.#menu = null;
        this.#menuIndex = -1;
        this.invalidate();
      }
    });
    this.#menu = menu;
    this.#menuIndex = index;
    this.invalidate();
  }

  openOverflow(): void {
    const desktop = this.desktop;
    if (!desktop || !this.hasOverflow) return;
    if (this.#menuIndex === TOOLBAR_OVERFLOW) return;
    this.closeMenu();

    const c = this.chevronRect;
    const anchor = this.screenBounds;
    const menu = showMenu(desktop, this.toMenuItems(this.overflowItems), {
      x: anchor.x + c.x,
      y: anchor.y + anchor.height,
    });
    if (!menu) return;
    menu.on("closed", () => {
      if (this.#menu === menu) {
        this.#menu = null;
        this.#menuIndex = -1;
        this.invalidate();
      }
    });
    this.#menu = menu;
    this.#menuIndex = TOOLBAR_OVERFLOW;
    this.invalidate();
  }

  closeMenu(): void {
    const menu = this.#menu;
    this.#menu = null;
    this.#menuIndex = -1;
    if (menu) this.desktop?.closePopup(menu);
  }

  /** Run an item: flip a toggle, then raise its click. */
  #invoke(item: ToolbarItemSpec): void {
    if (item.enabled === false || item.separator) return;
    if (item.toggle) item.checked = !item.checked;
    this.invalidate();
    this.emit("itemactivate", this.#items.indexOf(item));
    item.onClick?.(item);
  }

  // --- input ------------------------------------------------------------

  protected override hitTestSelf(): boolean {
    return true;
  }

  #setHover(index: number): void {
    if (this.#hover === index) return;
    this.#hover = index;
    this.invalidate();
  }

  #onPointerMove(e: MouseEventArgs): void {
    const d = this.desktop;
    if (!d) return;
    const index = this.indexAt({ x: e.x, y: e.y }, d.rc, d.theme);
    this.#setHover(index);
    // With one menu already down, sliding onto another drop-down switches to
    // it, the way a menu bar does.
    if (this.#menuIndex === -1 || index === this.#menuIndex) return;
    if (index === TOOLBAR_OVERFLOW) this.openOverflow();
    else if (index >= 0 && this.#items[index]?.menu && !this.#items[index]!.split) {
      this.openMenu(index);
    }
  }

  #onPointerDown(e: MouseEventArgs): void {
    const d = this.desktop;
    if (!d) return;
    const index = this.indexAt({ x: e.x, y: e.y }, d.rc, d.theme);
    if (index === -1) return;
    e.handled = true;

    if (index === TOOLBAR_OVERFLOW) {
      // Pressing the chevron while its menu is down closes it, so the pair
      // reads as a toggle rather than reopening under the pointer.
      if (this.#menuIndex === TOOLBAR_OVERFLOW) this.closeMenu();
      else this.openOverflow();
      return;
    }

    const item = this.#items[index]!;
    if (item.enabled === false) return;

    // Drop-downs open on the press; ordinary buttons fire on the release.
    if (item.menu && this.#onArrow(index, e.x, d.rc, d.theme)) {
      if (this.#menuIndex === index) this.closeMenu();
      else this.openMenu(index);
      return;
    }
    this.#pressed = index;
    this.invalidate();
  }

  #onClick(e: MouseEventArgs): void {
    const d = this.desktop;
    const pressed = this.#pressed;
    this.#pressed = -1;
    if (!d || pressed < 0) return;
    // Only fire when the release lands on the item the press started on.
    if (this.indexAt({ x: e.x, y: e.y }, d.rc, d.theme) !== pressed) return;
    this.#invoke(this.#items[pressed]!);
  }

  // --- painting ---------------------------------------------------------

  #stateFor(index: number, item: ToolbarItemSpec): VisualState {
    const enabled = item.enabled !== false;
    return {
      hovered: enabled && this.#hover === index,
      pressed:
        enabled && (this.#pressed === index || this.#menuIndex === index || item.checked === true),
      focused: false,
      disabled: !enabled,
    };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    this.#layout(rc, theme);
    rc.fillRect(this.localRect, p.windowBg);

    const font = theme.metrics.font;
    for (let i = 0; i < this.#visibleCount; i++) {
      const item = this.#items[i]!;
      const r = this.itemRect(i, rc, theme);

      if (item.separator) {
        rc.vLine(Math.round(r.x + r.width / 2), r.y + 3, r.height - 6, p.gridLine);
        continue;
      }

      const state = this.#stateFor(i, item);
      // Flat until you point at it, which is what keeps a toolbar quiet.
      if (state.hovered || state.pressed) theme.drawButton(rc, r, state);

      const ink = state.disabled ? p.textDisabled : p.text;
      const hasArrow = !!item.menu;
      const contentRight = r.x + r.width - BUTTON_PADDING - (hasArrow ? ARROW_WIDTH : 0);
      let x = r.x + BUTTON_PADDING;
      const midY = r.y + r.height / 2;

      if (this.#showsIcon(item) && item.icon) {
        rc.drawTexture(item.icon, {
          x,
          y: midY - this.iconSize / 2,
          width: this.iconSize,
          height: this.iconSize,
        });
        x += this.iconSize + (this.#showsText(item) ? ICON_TEXT_GAP : 0);
      }
      if (this.#showsText(item)) {
        const label = parseMnemonic(item.text ?? "").text;
        rc.drawText(label, x, midY, font, ink, {
          baseline: "middle",
          maxWidth: Math.max(0, contentRight - x),
        });
      }
      if (hasArrow) {
        if (item.split) rc.vLine(contentRight + 1, r.y + 4, r.height - 8, p.gridLine);
        drawTriangle(
          rc,
          { x: contentRight, y: r.y, width: ARROW_WIDTH + BUTTON_PADDING, height: r.height },
          "down",
          ink,
          3,
        );
      }
    }

    if (this.hasOverflow) {
      const c = this.chevronRect;
      const state: VisualState = {
        hovered: this.#hover === TOOLBAR_OVERFLOW,
        pressed: this.#menuIndex === TOOLBAR_OVERFLOW,
        focused: false,
        disabled: false,
      };
      if (state.hovered || state.pressed) theme.drawButton(rc, c, state);
      // A double chevron, the standard "there is more" mark.
      const cx = Math.round(c.x + c.width / 2);
      const cy = Math.round(c.y + c.height / 2);
      for (const dx of [-4, 0]) {
        for (let k = 0; k < 3; k++) {
          rc.fillRect({ x: cx + dx + k, y: cy - 3 + k, width: 1, height: 1 }, p.text);
          rc.fillRect({ x: cx + dx + k, y: cy + 3 - k, width: 1, height: 1 }, p.text);
        }
      }
    }

    rc.hLine(0, this.height - 1, this.width, p.gridLine);
  }
}
